import { setSelectors, addListeners } from './utilities.js';
import { addItemToCart } from '@umbrella/cart-service';

class RCAtcButton extends HTMLElement {

  constructor() {
    super();
    this.events = {};
    this.selectors = {};
    this.isSubmitting = false;
    this.updateDisabledState = () => { this._updateDisabledStateImpl(); };
    this._onVariantChange = (event) => {
      const variant = event && event.detail && event.detail.data;
      if(variant && this.selectors.variantIdInput) {
        this.selectors.variantIdInput.value = variant.id;
      };
      this._toggleBisTrigger(variant);
      this.updateDisabledState();
    };
    this.qtyObserver = null;
  };

  connectedCallback() { this._initialize(); };

  disconnectedCallback() { this._deinitialize(); };

  _setButtonState(disabled, label){
    if(!this.selectors.submitButton) return;
    this.selectors.submitButton.disabled = disabled;
    const target = disabled ? this.selectors.disabledTextEl : this.selectors.defaultTextEl;
    if(target) target.textContent = label;
  };

  _updateDisabledStateImpl(){
    if(!this.selectors.submitButton) return;
    const { qtyInput, qtyWrapper } = this.selectors;
    const outOfStockLabel = window.umbrellaConfig.addToCart.out_of_stock;
    const defaultLabel = window.umbrellaConfig.addToCart.default;
    const continueSelling = !!(qtyWrapper && qtyWrapper.hasAttribute('continue-selling'));

    if(continueSelling) {
      return this._setButtonState(false, defaultLabel);
    } else if(qtyInput && qtyInput.disabled){
      return this._setButtonState(true, outOfStockLabel);
    } else {
      // Determine remaining from current max (updated by rc-quantity-input) vs min
      const disable = this._computeDisableFromQuantity(qtyInput);
      this._setButtonState(!!disable, disable ? outOfStockLabel : defaultLabel);
    }
  };

  _toggleBisTrigger(variant){
    if(!variant || !this.selectors.form) return;
    const wrapper = this.selectors.form.querySelector('[data-bis-wrapper]');
    if(!wrapper) return;
    if(variant.available){
      wrapper.setAttribute('hidden', '');
      wrapper.style.setProperty('display', 'none', 'important');
    } else {
      wrapper.removeAttribute('hidden');
      wrapper.style.removeProperty('display');
    }
  };

  _computeDisableFromQuantity(qtyInput){
    if(!qtyInput) return false;
    const minAttr = qtyInput.getAttribute('min');
    const maxAttr = qtyInput.getAttribute('max');
    const min = minAttr !== null ? Number(minAttr) : 1;
    const max = maxAttr ? Number(maxAttr) : Infinity;
    return Number.isFinite(max) ? (max <= 0 || max < min) : false;
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if(this.isSubmitting) return;
    if(!this.selectors.form || !this.selectors.variantIdInput) return;
    const variantId = this.selectors.variantIdInput.value;
    if(!variantId) {
      showToast('Please select a variant before adding to cart.', 'error');
      return;
    };
    const quantityInput = this.selectors.qtyInput || this.selectors.form.querySelector('input[name="quantity"]');
    let quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;
    // Clamp to remaining if inventory policy is deny
    const qtyMaxAttr = quantityInput && quantityInput.getAttribute('max');
    const maxFromInput = qtyMaxAttr ? Number(qtyMaxAttr) : Infinity;
    const continueSelling = !!(this.selectors.form.querySelector('rc-quantity-input[continue-selling]'));
    if(!continueSelling && Number.isFinite(maxFromInput)){
      const remaining = Math.max(0, maxFromInput);
      if(remaining <= 0){
        showToast(`Requested ${quantity} exceeds available stock of ${remaining}.`, 'error');
        return; // block submit
      };
      if(quantity > remaining){
        quantity = remaining;
        if(quantityInput) quantityInput.value = String(quantity);
        showToast(`Only ${remaining} available; adding ${remaining}.`, 'warning');
      };
    };
    this.isSubmitting = true;
    const loadingText = (window.umbrellaConfig && window.umbrellaConfig.addToCart && window.umbrellaConfig.addToCart.submit) || 'Adding...';
    this._setButtonState(true, loadingText);
    try {
      const item = await addItemToCart(variantId, quantity, new FormData(this.selectors.form));
      if(item && this.selectors.qtyInput){
        // Reset quantity back to 1 after a successful add (UI only)
        this.selectors.qtyInput.value = '1';
      }
    } finally {
      this.isSubmitting = false;
      this.updateDisabledState();
    };
  };

  _initialize() {
    this.events = window.umbrellaConfig.events;
    this.selectors = setSelectors(this, {
      submitButton: 'button[type="submit"]',
      defaultTextEl: '[data-default-text]',
      disabledTextEl: '[data-disabled-text]'
    });
    const formEl = this.closest('form');
    this.selectors.form = formEl || null;
    if(!this.selectors.form) return;
    this.selectors.form.addEventListener('submit', this.handleSubmit);
    this.selectors.variantIdInput = this.selectors.form.querySelector('input[name="id"]');
    this.selectors.qtyWrapper = this.selectors.form.querySelector('rc-quantity-input');
    this.selectors.qtyInput = this.selectors.form.querySelector('input[name="quantity"]');
    if(!this.selectors.variantIdInput) return;
    addListeners(document, {
      [this.events.cartChange]: this.updateDisabledState,
      [this.events.variantChange]: this._onVariantChange
    });
    if(this.selectors.qtyWrapper && this.selectors.qtyInput && window.MutationObserver){
      this.qtyObserver = new MutationObserver(() => { this.updateDisabledState(); });
      this.qtyObserver.observe(this.selectors.qtyInput, { attributes: true, attributeFilter: ['max','disabled','value'] });
      this.selectors.qtyWrapper.addEventListener(this.events.quantityChange, this.updateDisabledState);
    };
    this.updateDisabledState();
  };

  _deinitialize() {
    if(this.selectors.form) this.selectors.form.removeEventListener('submit', this.handleSubmit);
    if(this.events && this.events.cartChange) document.removeEventListener(this.events.cartChange, this.updateDisabledState);
    if(this.events && this.events.variantChange) document.removeEventListener(this.events.variantChange, this._onVariantChange);
    if(this.qtyObserver){ try { this.qtyObserver.disconnect(); } catch(e){} this.qtyObserver = null; }
    if(this.selectors.qtyWrapper && this.events && this.events.quantityChange){
      this.selectors.qtyWrapper.removeEventListener(this.events.quantityChange, this.updateDisabledState);
    };
  };
    
};
  
if(!customElements.get('rc-atc-button')) customElements.define('rc-atc-button', RCAtcButton);